import { memo, useEffect } from 'react';
import { useAsync } from 'react-use';

import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  errorApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { NotFoundError } from '@backstage/errors';
import {
  catalogApiRef,
  humanizeEntityRef,
} from '@backstage/plugin-catalog-react';

import Autocomplete from '@mui/material/Autocomplete';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';

const GROUP_KIND = 'supervisory-org';

const entityRefDefaults = {
  defaultKind: 'Group',
  defaultNamespace: 'default',
};

type OwnerSectionProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
};

export const OwnerSection = memo(
  ({ value, onChange, disabled }: OwnerSectionProps) => {
    const identityApi = useApi(identityApiRef);
    const catalogApi = useApi(catalogApiRef);
    const errorApi = useApi(errorApiRef);

    const { value: groups, loading } = useAsync(async () => {
      const { userEntityRef } = await identityApi.getBackstageIdentity();

      if (!userEntityRef) {
        errorApi.post(new NotFoundError('No user entity ref found'));
        return [];
      }

      const { items } = await catalogApi.getEntities({
        filter: {
          kind: 'Group',
          'relations.hasMember': userEntityRef,
          'spec.type': GROUP_KIND,
        },
        fields: [
          'metadata.name',
          'metadata.namespace',
          'metadata.title',
          'kind',
        ],
      });

      return items;
    });

    useEffect(() => {
      if (groups && groups.length > 0 && !value) {
        onChange(stringifyEntityRef(groups[0]));
      }
    }, [groups, value, onChange]);

    const selectedGroup =
      groups?.find(g => stringifyEntityRef(g) === value) ?? null;

    return (
      <FormControl fullWidth variant="standard" required error={!value}>
        <Autocomplete
          id="owner"
          value={selectedGroup}
          loading={loading}
          disabled={disabled}
          options={groups ?? []}
          getOptionLabel={option =>
            option.metadata.title ??
            humanizeEntityRef(option, entityRefDefaults) ??
            option.metadata.name
          }
          getOptionKey={option => stringifyEntityRef(option)}
          isOptionEqualToValue={(option, val) =>
            stringifyEntityRef(option) === stringifyEntityRef(val)
          }
          onChange={(_, val) => {
            onChange(val ? stringifyEntityRef(val) : (undefined as any));
          }}
          renderInput={params => (
            <TextField
              {...params}
              label="Business Owner"
              size="small"
              variant="outlined"
              required
              error={!value}
            />
          )}
          loadingText="Loading groups..."
          noOptionsText="No groups found. You may not be a member of any supervisory org group."
        />
        <FormHelperText>The team that owns this tenant</FormHelperText>
      </FormControl>
    );
  },
);
